import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useMultiSelect } from '@cbhq/cds-common/select/useMultiSelect';
import { Button } from '@cbhq/cds-web/buttons';
import { Box, VStack } from '@cbhq/cds-web/layout';
import { Spinner } from '@cbhq/cds-web/loaders';
import { Modal, ModalBody, ModalHeader } from '@cbhq/cds-web/overlays';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { TextBody, TextLabel2, TextTitle1 } from '@cbhq/cds-web/typography';
import { ActionType, ComponentType, logEvent, markStep } from '@cbhq/client-analytics';
import { useGetWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import useIsMobile from '../../../hooks/useIsMobile';
import { EntityRoleDropdown } from '../components/EntityRoleDropdown';
import { LegalEntityDropdown } from '../components/LegalEntityDropdown';
import { MemberAvatar } from '../components/MemberAvatar';
import { useTeamApi } from '../useTeamApi';
import { canManageMembers } from '../utils/canManageMembers';
import type { Member as TeamMember, MemberRole } from '../utils/constants';
import { ROUTE_TEAM } from '../utils/constants';
import { useRoles } from '../utils/useRoles';

const messages = defineMessages({
  closeAccessibilityLabel: {
    id: 'Team.Member.closeAccessibilityLabel',
    defaultMessage: 'Close',
    description: 'Accessibility label for the close button on the member modal',
  },
  editMemberRoleSuccess: {
    id: 'Team.editMemberRoleSuccess',
    defaultMessage: 'Membership updated',
    description: 'Edit member membership success',
  },
  editMemberRolePartialError: {
    id: 'Team.editMemberRolePartialError',
    defaultMessage: 'Failed to update membership for: {entityNames}. Other entities were updated.',
    description: 'Edit member membership partial failure — some entities succeeded, others did not',
  },
  editMemberRoleError: {
    id: 'Team.editMemberRoleError',
    defaultMessage: 'Error updating membership',
    description: 'Edit member membership error',
  },
  editMemberRoleSaveButton: {
    id: 'Team.editMemberRoleSaveButton',
    defaultMessage: 'Save',
    description: 'Edit member role save button',
  },
});

export function Member() {
  const isMobile = useIsMobile();
  const currentUser = useUserProfile();
  const { formatMessage } = useIntl();
  const { user_uuid: userUuid } = useParams<{ user_uuid: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';

  // useGetWorkspaceMembers is cached by organizationId — Members.tsx has already called this
  // with the same argument, so this does not fire an additional network request.
  const orgMembersData = useGetWorkspaceMembers(
    { organizationId },
    { skip: organizationId.length === 0 },
  );

  const rawMember = useMemo(
    () => orgMembersData?.members?.find((m) => m.userUuid === userUuid) ?? null,
    [orgMembersData, userUuid],
  );

  const [didInitializeSelections, setDidInitializeSelections] = useState(false);

  useEffect(() => {
    setDidInitializeSelections(false);
  }, [userUuid]);

  const teamMember = useMemo<TeamMember | null>(() => {
    if (!rawMember) {
      return null;
    }
    return {
      userUuid: rawMember.userUuid ?? '',
      firstName: rawMember.firstName ?? '',
      lastName: rawMember.lastName ?? '',
      email: rawMember.email ?? '',
      roles: (rawMember.legalEntityRoles?.flatMap((ler) => ler.roles ?? []) ?? []) as MemberRole[],
      avatarUrl: rawMember.avatarUrl,
    };
  }, [rawMember]);
  const [lastLoadedTeamMember, setLastLoadedTeamMember] = useState<TeamMember | null>(null);

  // canManageMembers only inspects members[0].roles (the current user).
  // Passing only the first element avoids a full array allocation.
  const canManage = useMemo(() => {
    const firstMember = orgMembersData?.members?.[0];
    const leRoles = (firstMember?.legalEntityRoles?.flatMap((ler) => ler.roles ?? []) ??
      []) as MemberRole[];
    const orgRoles = firstMember?.roles as string[] | undefined;
    return canManageMembers(
      [{ userUuid: '', firstName: '', lastName: '', email: '', roles: leRoles }],
      orgRoles,
    );
  }, [orgMembersData?.members]);

  // ---- Entity selector ----
  const memberEntityIds = useMemo(
    () => rawMember?.legalEntityRoles?.map((ler) => ler.legalEntityId ?? '').filter(Boolean) ?? [],
    [rawMember],
  );

  const {
    value: selectedEntityIds,
    onChange: onEntityIdsChange,
    resetSelection: resetEntitySelection,
    addSelection: addEntitySelection,
  } = useMultiSelect<string>({ initialValue: [] });

  // ---- EditMemberRole logic ----
  const { updateMemberRoles } = useTeamApi(organizationId);
  const { supportedRoles, hasLoadedRoleDefinitions } = useRoles();

  // Only initialize the selector with roles the user can see and interact with.
  const initialRoles = useMemo(
    () => (teamMember?.roles ?? []).filter((role) => supportedRoles.includes(role)),
    [teamMember?.roles, supportedRoles],
  );

  // Roles outside supportedRoles (e.g. FF-gated or different product scope) must be preserved
  // when saving because updateOrganizationMemberRole() replaces the full role set for each entity.
  const hiddenRoles = useMemo(
    () => (teamMember?.roles ?? []).filter((role) => !supportedRoles.includes(role)),
    [teamMember?.roles, supportedRoles],
  );

  const {
    value: roles,
    onChange: onRolesChange,
    resetSelection,
    addSelection,
  } = useMultiSelect<MemberRole>({
    initialValue: initialRoles,
  });

  const hasRolesChanged = useMemo(() => {
    if (roles.length !== initialRoles.length) {
      return true;
    }
    const initialRolesSet = new Set(initialRoles);
    return roles.some((role) => !initialRolesSet.has(role));
  }, [roles, initialRoles]);

  const isCurrentUser = useMemo(
    () =>
      Boolean(
        rawMember?.email &&
          currentUser?.email &&
          rawMember.email.toLowerCase() === currentUser.email.toLowerCase(),
      ),
    [rawMember?.email, currentUser?.email],
  );

  const hasEntitiesChanged = useMemo(() => {
    if (selectedEntityIds.length !== memberEntityIds.length) {
      return true;
    }
    const memberEntitySet = new Set(memberEntityIds);
    return selectedEntityIds.some((id) => !memberEntitySet.has(id));
  }, [selectedEntityIds, memberEntityIds]);

  const isSaveDisabled =
    (isCurrentUser && selectedEntityIds.length === 0) ||
    (selectedEntityIds.length > 0 && roles.length === 0) ||
    (!hasRolesChanged && !hasEntitiesChanged);

  useEffect(() => {
    // Wait for role definitions to load before initializing so supportedRoles is not empty,
    // which would leave valid roles appearing unassigned.
    if (!rawMember || didInitializeSelections || !hasLoadedRoleDefinitions) {
      return;
    }

    resetEntitySelection();
    addEntitySelection(memberEntityIds);
    resetSelection();
    addSelection((teamMember?.roles ?? []).filter((role) => supportedRoles.includes(role)));
    setDidInitializeSelections(true);
  }, [
    rawMember,
    didInitializeSelections,
    hasLoadedRoleDefinitions,
    memberEntityIds,
    teamMember?.roles,
    supportedRoles,
    resetEntitySelection,
    addEntitySelection,
    resetSelection,
    addSelection,
  ]);

  const handleRoleChange = useCallback(
    (newValues: MemberRole | MemberRole[] | null) => {
      onRolesChange(newValues);
    },
    [onRolesChange],
  );

  const handleClose = useCallback(() => {
    logEvent('member_info_page_closed', {
      componentType: ComponentType.modal,
      action: ActionType.dismiss,
    });

    if (hasRolesChanged || hasEntitiesChanged) {
      logEvent('member_info_page_closed_with_unsaved_changes', {
        componentType: ComponentType.modal,
        action: ActionType.dismiss,
      });
    }

    navigate(ROUTE_TEAM);
  }, [hasEntitiesChanged, hasRolesChanged, navigate]);

  const [isRoleSaveLoading, setIsRoleSaveLoading] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);
  const displayTeamMember = teamMember ?? (isRoleSaveLoading ? lastLoadedTeamMember : null);

  useEffect(() => {
    if (teamMember) {
      setLastLoadedTeamMember(teamMember);
    }
  }, [teamMember]);

  const handleSave = useCallback(async () => {
    if (!userUuid || (selectedEntityIds.length > 0 && roles.length === 0)) {
      return;
    }

    setIsRoleSaveLoading(true);
    setRoleError(null);

    try {
      markStep('submit_workspace_member_update');
      logEvent('member_role_changed', {
        componentType: ComponentType.button,
        action: ActionType.click,
      });

      await updateMemberRoles({
        memberUuid: userUuid,
        selectedEntityIds,
        roles: [...roles, ...hiddenRoles],
        getPartialErrorMessage: (entityNames) =>
          formatMessage(messages.editMemberRolePartialError, { entityNames }),
        getFullErrorMessage: () => formatMessage(messages.editMemberRoleError),
      });

      logEvent('member_role_updated_success', {
        componentType: ComponentType.api,
        action: ActionType.api,
      });
      markStep('workspace_member_update_success');
      toast.show(formatMessage(messages.editMemberRoleSuccess));
      logEvent('member_modal_closed_after_save', {
        componentType: ComponentType.modal,
        action: ActionType.dismiss,
      });
      navigate(ROUTE_TEAM);
    } catch (err) {
      setDidInitializeSelections(false);
      setRoleError(
        err instanceof Error ? err.message : formatMessage(messages.editMemberRoleError),
      );
      logEvent('member_role_updated_failed', {
        componentType: ComponentType.api,
        action: ActionType.api,
      });
    } finally {
      setIsRoleSaveLoading(false);
    }
  }, [
    roles,
    hiddenRoles,
    userUuid,
    selectedEntityIds,
    updateMemberRoles,
    formatMessage,
    toast,
    navigate,
  ]);

  useEffect(() => {
    logEvent('member_info_page_viewed', {
      componentType: ComponentType.page,
      action: ActionType.view,
    });
  }, []);

  // Redirect if organizationId is missing — spinner would freeze otherwise (skip prevents fetch).
  useEffect(() => {
    if (!organizationId) {
      navigate({ pathname: ROUTE_TEAM }, { replace: true });
    }
  }, [organizationId, navigate]);

  // Redirect if member not found once data has loaded
  useEffect(() => {
    if (orgMembersData && !rawMember && !isRoleSaveLoading && !roleError) {
      navigate(ROUTE_TEAM, { replace: true });
    }
  }, [navigate, orgMembersData, rawMember, isRoleSaveLoading, roleError]);

  if (!displayTeamMember) {
    return (
      <Modal onRequestClose={handleClose} visible width={isMobile ? '100%' : 480}>
        <ModalHeader
          closeAccessibilityLabel={formatMessage(messages.closeAccessibilityLabel)}
          // Member name is shown as TextTitle1 in the body; the header is just the close button.
          title=""
        />
        <ModalBody>
          <Box alignItems="center" justifyContent="center" padding={6}>
            <Spinner size={4} color="fgPrimary" />
          </Box>
        </ModalBody>
      </Modal>
    );
  }

  return (
    <Modal onRequestClose={handleClose} visible width={isMobile ? '100%' : 480}>
      <ModalHeader
        closeAccessibilityLabel={formatMessage(messages.closeAccessibilityLabel)}
        title=""
      />
      <ModalBody>
        <VStack gap={2} testID="team-member-page">
          <VStack alignItems="center" gap={2}>
            <MemberAvatar member={displayTeamMember} size="xxxl" />

            <VStack alignItems="center" gap={0.5}>
              <TextTitle1 as="p" textAlign="center">
                {displayTeamMember.firstName} {displayTeamMember.lastName}
              </TextTitle1>
              <TextBody as="p" textAlign="center">
                {displayTeamMember.email}
              </TextBody>
            </VStack>
          </VStack>

          <VStack gap={4} paddingX={3} testID="team-member-role-update-page">
            <VStack gap={1}>
              <EntityRoleDropdown
                value={roles}
                onChange={handleRoleChange}
                testID="team-member-role-update-select"
                disabled={!canManage}
              />

              <LegalEntityDropdown
                value={selectedEntityIds}
                onChange={onEntityIdsChange}
                testID="team-member-entity-select"
                disabled={!canManage}
              />

              {roleError && (
                <TextLabel2 as="span" color="fgNegative">
                  {roleError}
                </TextLabel2>
              )}
            </VStack>

            {canManage && (
              <Button
                onClick={handleSave}
                testID="team-member-role-update-save-button"
                disabled={isSaveDisabled}
                loading={isRoleSaveLoading}
              >
                {formatMessage(messages.editMemberRoleSaveButton)}
              </Button>
            )}
          </VStack>
        </VStack>
      </ModalBody>
    </Modal>
  );
}
